import React, { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Card,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormLabel,
  Modal,
  ModalDialog,
  Option,
  Select,
  Sheet,
  Skeleton,
  Snackbar,
  Stack,
  Typography,
} from "@mui/joy";
import {
  Plus as AddIcon,
  ArrowsClockwise as RefreshIcon,
} from "@phosphor-icons/react";
import { useAuth } from "../../contexts/AuthContext";
import {
  formatMoney,
  mailBatchesApi,
  mailerPinsApi,
} from "../../lib/directMailApi";
import { getApiErrorMessage } from "../../lib/apiError";
import type { MailBatchStatus } from "../../types/directMail";
import {
  DirectMailPageHeader,
  DIRECT_MAIL_ROUTES,
} from "../../components/directmail/DirectMailNav";
import {
  BatchCard,
  type MailBatchRow,
} from "../../components/directmail/BatchCard";
import { CreateBatchModal } from "../../components/directmail/CreateBatchModal";
import { BatchWorkspace } from "../../components/directmail/BatchWorkspace";
import { loadAllPages } from "../../components/directmail/loadAllPages";

/** How long to keep polling a queued render before telling the user to check back. */
const RENDER_TIMEOUT_MS = 3 * 60 * 1000;
const RENDER_POLL_MS = 4000;

interface RenderWatch {
  startedAt: number;
  /** The batch's updated_at when the render was queued, so a re-render is detectable. */
  updatedAt: string;
}

interface SnackbarState {
  open: boolean;
  message: string;
  color: "success" | "danger" | "warning" | "neutral";
}

export const DirectMailBatchesPage: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isOrgAdmin = Boolean(user?.is_organization_admin || user?.is_superuser);

  const [statusFilter, setStatusFilter] = useState<MailBatchStatus | null>(
    null,
  );
  const [createOpen, setCreateOpen] = useState(false);
  const [workspaceId, setWorkspaceId] = useState<number | null>(null);
  const [submitTarget, setSubmitTarget] = useState<MailBatchRow | null>(null);
  const [cancelTarget, setCancelTarget] = useState<MailBatchRow | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [watching, setWatching] = useState<Record<number, RenderWatch>>({});
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: "",
    color: "neutral",
  });

  const notify = (message: string, color: SnackbarState["color"]) =>
    setSnackbar({ open: true, message, color });

  // The whole list, not the first page: this page has no paging control, and
  // the API ignores `page_size`, so a single request would hide every batch
  // older than the twenty most recent.
  const batchesQuery = useQuery({
    queryKey: ["direct-mail", "batches", statusFilter],
    queryFn: () =>
      loadAllPages((page) =>
        mailBatchesApi.list({ status: statusFilter ?? undefined, page }),
      ),
    refetchInterval: Object.keys(watching).length > 0 ? RENDER_POLL_MS : false,
  });

  const approvedQuery = useQuery({
    queryKey: ["direct-mail", "pins", "approved-count"],
    queryFn: () => mailerPinsApi.list({ status: "approved" }),
  });

  const batches = (batchesQuery.data ?? []) as MailBatchRow[];

  useEffect(() => {
    if (Object.keys(watching).length === 0) return;

    const ready: string[] = [];
    const timedOut: string[] = [];
    const stillWatching: Record<number, RenderWatch> = {};

    Object.entries(watching).forEach(([key, watch]) => {
      const id = Number(key);
      const batch = batches.find((entry) => entry.id === id);
      const proofUrl = batch?.proof_pdf_url ?? batch?.proof_pdf ?? null;
      if (batch && proofUrl && batch.updated_at !== watch.updatedAt) {
        ready.push(batch.reference);
        return;
      }
      if (batch?.error_message) {
        timedOut.push(batch.reference);
        return;
      }
      if (Date.now() - watch.startedAt > RENDER_TIMEOUT_MS) {
        timedOut.push(batch?.reference ?? String(id));
        return;
      }
      stillWatching[id] = watch;
    });

    if (ready.length === 0 && timedOut.length === 0) return;
    setWatching(stillWatching);
    if (ready.length > 0) {
      notify(`Proof ready for ${ready.join(", ")}.`, "success");
    }
    if (timedOut.length > 0) {
      notify(
        `The proof for ${timedOut.join(", ")} is taking longer than expected — you will be notified when it finishes.`,
        "warning",
      );
    }
  }, [batches, watching]);

  const refresh = () => {
    void queryClient.invalidateQueries({
      queryKey: ["direct-mail", "batches"],
    });
    void queryClient.invalidateQueries({ queryKey: ["direct-mail", "pins"] });
  };

  const renderProof = async (batch: MailBatchRow) => {
    setBusyId(batch.id);
    try {
      await mailBatchesApi.renderProof(batch.id);
      setWatching((current) => ({
        ...current,
        [batch.id]: { startedAt: Date.now(), updatedAt: batch.updated_at },
      }));
      notify(`Rendering the proof for ${batch.reference}…`, "neutral");
    } catch (err) {
      notify(
        getApiErrorMessage(err, "That proof could not be rendered."),
        "danger",
      );
    } finally {
      setBusyId(null);
    }
  };

  const submitBatch = async () => {
    if (!submitTarget) return;
    setBusyId(submitTarget.id);
    try {
      const updated = await mailBatchesApi.submit(submitTarget.id);
      notify(
        `${updated.reference} submitted — ${updated.piece_count} pieces for ${formatMoney(updated.total_amount)}.`,
        "success",
      );
      setSubmitTarget(null);
      refresh();
    } catch (err) {
      refresh();
      setSubmitTarget(null);
      // A 402 says exactly what is wrong with the card; show it as written.
      notify(
        getApiErrorMessage(err, "That batch could not be submitted."),
        "danger",
      );
    } finally {
      setBusyId(null);
    }
  };

  const cancelBatch = async () => {
    if (!cancelTarget) return;
    setBusyId(cancelTarget.id);
    try {
      await mailBatchesApi.cancel(cancelTarget.id);
      notify(
        `${cancelTarget.reference} cancelled. Its pins are approved again.`,
        "success",
      );
      setCancelTarget(null);
      refresh();
    } catch (err) {
      notify(
        getApiErrorMessage(err, "That batch could not be cancelled."),
        "danger",
      );
    } finally {
      setBusyId(null);
    }
  };

  const approvedCount = approvedQuery.data?.count ?? 0;

  return (
    <Box>
      <DirectMailPageHeader
        title="Batches"
        description="A batch is one mailing: approved pins printed together, paid for in one charge."
        actions={
          <>
            <Button
              variant="outlined"
              color="neutral"
              startDecorator={<RefreshIcon />}
              onClick={refresh}
              loading={batchesQuery.isFetching && !batchesQuery.isLoading}
            >
              Refresh
            </Button>
            <Button
              startDecorator={<AddIcon />}
              onClick={() => setCreateOpen(true)}
            >
              Create batch
            </Button>
          </>
        }
      />

      <Card variant="soft" sx={{ p: 2, mb: 2 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          justifyContent="space-between"
          alignItems={{ sm: "center" }}
        >
          <Box>
            <Typography level="title-sm">
              {approvedQuery.isLoading
                ? "Checking approved pins…"
                : `${approvedCount} pin${approvedCount === 1 ? "" : "s"} approved and waiting`}
            </Typography>
            <Typography level="body-sm" sx={{ color: "text.tertiary" }}>
              {approvedCount === 0
                ? "Measure pins in the queue and approve their prices — approved pins are what a batch is built from."
                : "Group them into a batch by campaign, review the proof, then mail."}
            </Typography>
          </Box>
          <Button
            component={RouterLink}
            to={DIRECT_MAIL_ROUTES.queue}
            variant="outlined"
            color="neutral"
            size="sm"
          >
            Open the queue
          </Button>
        </Stack>
      </Card>

      <FormControl sx={{ maxWidth: 240, mb: 2 }}>
        <FormLabel>Status</FormLabel>
        <Select
          value={statusFilter}
          onChange={(_, value) => setStatusFilter(value)}
          slotProps={{ button: { "aria-label": "Filter batches by status" } }}
        >
          <Option value={null}>All batches</Option>
          <Option value="draft">Draft</Option>
          <Option value="submitted">Submitted</Option>
          <Option value="printing">Printing</Option>
          <Option value="mailed">Mailed</Option>
          <Option value="failed">Failed</Option>
          <Option value="cancelled">Cancelled</Option>
        </Select>
      </FormControl>

      {batchesQuery.isLoading ? (
        <Stack spacing={1.5}>
          {[0, 1].map((row) => (
            <Skeleton
              key={row}
              variant="rectangular"
              height={180}
              sx={{ borderRadius: "sm" }}
            />
          ))}
        </Stack>
      ) : batchesQuery.isError ? (
        <Alert color="danger" variant="soft">
          <Box>
            <Typography level="title-sm">
              Batches could not be loaded
            </Typography>
            <Typography level="body-sm">
              {getApiErrorMessage(
                batchesQuery.error,
                "Please try again in a moment.",
              )}
            </Typography>
            <Button
              size="sm"
              variant="plain"
              sx={{ px: 0, mt: 0.5 }}
              onClick={() => void batchesQuery.refetch()}
            >
              Retry
            </Button>
          </Box>
        </Alert>
      ) : batches.length === 0 ? (
        <Sheet
          variant="soft"
          sx={{ p: 4, borderRadius: "sm", textAlign: "center" }}
        >
          <Typography level="title-md">
            {statusFilter ? "No batches with that status" : "No batches yet"}
          </Typography>
          <Typography
            level="body-sm"
            sx={{ color: "text.tertiary", mt: 1, mb: 2 }}
          >
            {statusFilter
              ? "Clear the filter to see every batch."
              : "Once you have approved pins, create a batch to print and mail them together. Creating a draft costs nothing."}
          </Typography>
          <Button
            startDecorator={<AddIcon />}
            disabled={approvedCount === 0}
            onClick={() => setCreateOpen(true)}
          >
            Create batch
          </Button>
        </Sheet>
      ) : (
        <Stack spacing={1.5}>
          {batches.map((batch) => (
            <BatchCard
              key={batch.id}
              batch={batch}
              isOrgAdmin={isOrgAdmin}
              rendering={Boolean(watching[batch.id])}
              busy={busyId === batch.id}
              onRenderProof={(target) => void renderProof(target)}
              onSubmit={(target) => setSubmitTarget(target)}
              onCancel={(target) => setCancelTarget(target)}
              onWorkspace={(target) => setWorkspaceId(target.id)}
            />
          ))}
        </Stack>
      )}

      <BatchWorkspace
        batchId={workspaceId}
        onClose={() => setWorkspaceId(null)}
        isAdmin={isOrgAdmin}
      />
      <CreateBatchModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(batch) => {
          setCreateOpen(false);
          // The server drops houses it will not mail — do-not-knock, now a
          // customer, or claimed by another batch first — and says why per pin.
          // Reporting only the count left the user to work out which of their
          // selection went missing, and why.
          const rejected = batch.rejected ?? [];
          const created = `Draft batch ${batch.reference} created — ${batch.piece_count} pieces, ${formatMoney(batch.total_amount)}.`;
          notify(
            rejected.length > 0
              ? `${created} ${rejected.length} left out: ${rejected[0].reason}${rejected.length > 1 ? ` (and ${rejected.length - 1} more)` : ""}`
              : `${created} Render the proof next.`,
            rejected.length > 0 ? "warning" : "success",
          );
          refresh();
        }}
      />

      <Modal open={submitTarget !== null} onClose={() => setSubmitTarget(null)}>
        <ModalDialog sx={{ width: { xs: "92vw", sm: 460 } }}>
          <DialogTitle>Submit this batch to print?</DialogTitle>
          <DialogContent>
            <Stack spacing={1.5} sx={{ pt: 1 }}>
              <Typography level="body-sm">
                {submitTarget?.campaign_name} · {submitTarget?.reference}
              </Typography>
              <Sheet variant="soft" sx={{ p: 1.5, borderRadius: "sm" }}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography level="body-sm">Pieces</Typography>
                  <Typography level="body-sm">
                    {submitTarget?.piece_count}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography level="body-sm">Price per piece</Typography>
                  <Typography level="body-sm">
                    {formatMoney(submitTarget?.unit_price)}
                  </Typography>
                </Stack>
                <Divider sx={{ my: 1 }} />
                <Stack direction="row" justifyContent="space-between">
                  <Typography level="title-sm">
                    {submitTarget?.paid_at
                      ? "Additional charge"
                      : "Charged to your card on file"}
                  </Typography>
                  <Typography level="title-sm">
                    {formatMoney(
                      submitTarget?.paid_at ? 0 : submitTarget?.total_amount,
                    )}
                  </Typography>
                </Stack>
              </Sheet>
              <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                Submitting hands the batch to the print-and-mail provider. It
                cannot be cancelled afterwards.
              </Typography>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              loading={busyId === submitTarget?.id}
              onClick={() => void submitBatch()}
            >
              {submitTarget?.paid_at
                ? "Retry using existing payment"
                : `Charge ${formatMoney(submitTarget?.total_amount)} and mail`}
            </Button>
            <Button
              variant="plain"
              color="neutral"
              onClick={() => setSubmitTarget(null)}
            >
              Cancel
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      <Modal open={cancelTarget !== null} onClose={() => setCancelTarget(null)}>
        <ModalDialog sx={{ width: { xs: "92vw", sm: 440 } }}>
          <DialogTitle>
            {cancelTarget?.status === "draft"
              ? "Cancel this draft?"
              : "Cancel this failed batch?"}
          </DialogTitle>
          <DialogContent>
            <Typography level="body-sm">
              {cancelTarget?.piece_count} pins go back to approved and can be
              batched again later.
              {cancelTarget?.status === "draft"
                ? " Nothing has been charged."
                : " This batch was already charged — cancelling frees its houses but does not refund it." +
                  " Contact support with the batch reference."}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              color="danger"
              loading={busyId === cancelTarget?.id}
              onClick={() => void cancelBatch()}
            >
              Cancel batch
            </Button>
            <Button
              variant="plain"
              color="neutral"
              onClick={() => setCancelTarget(null)}
            >
              Keep it
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      <Snackbar
        open={snackbar.open}
        color={snackbar.color}
        variant="soft"
        autoHideDuration={6000}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
      >
        {snackbar.message}
      </Snackbar>
    </Box>
  );
};
